import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SendReminderResponse } from '@academy/types'
import { api } from '../lib/api.js'
import { friendlyError } from '../lib/errors.js'
import { PageHeader } from '../components/PageHeader.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { LinkButton } from '../components/Button.js'
import { Input } from '../components/Field.js'
import { StatusPill } from '../components/StatusPill.js'
import { Money } from '../components/Money.js'
import { Card, CardBody } from '../components/Card.js'
import { ReminderDialog } from '../components/ReminderDialog.js'
import { RecordPaymentDialog } from '../components/RecordPaymentDialog.js'

type StatusFilter = 'all' | 'pending' | 'partial' | 'overdue' | 'paid' | 'waived'

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'partial', label: 'Partial' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'paid', label: 'Paid' },
  { key: 'waived', label: 'Waived' },
]

export const Route = createFileRoute('/_app/fees/')({
  component: FeesPage,
})

export interface InvoiceRow {
  id: string
  invoiceNo: string
  studentId: string
  studentName: string
  parentId: string
  parentName: string | null
  feeCategoryId: string | null
  feeCategoryName: string | null
  amountInr: number
  discountInr: number
  finalAmountInr: number
  amountPaidInr: number
  balanceInr: number
  dueDate: string
  status: 'pending' | 'partial' | 'paid' | 'overdue' | 'waived'
  paymentMethod: 'razorpay' | 'cash' | 'upi' | null
  paidAt: string | null
  createdAt: string
  parentHasEmail: boolean
  parentHasPhone: boolean
  academyWantsEmail: boolean
  academyWantsWhatsapp: boolean
}

export type { SendReminderResponse }

function FeesPage() {
  const qc = useQueryClient()

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  // Debounce the search box so we don't fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => clearTimeout(t)
  }, [search])

  const invoices = useQuery({
    queryKey: ['fee-invoices', { q: debouncedSearch, status: statusFilter }],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (debouncedSearch) params.set('q', debouncedSearch)
      if (statusFilter !== 'all') params.set('status', statusFilter)
      const qs = params.toString()
      const res = await api<InvoiceRow[]>(`/api/v1/fee-invoices${qs ? `?${qs}` : ''}`)
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const deleteInvoice = useMutation({
    mutationFn: async (id: string) => {
      const res = await api(`/api/v1/fee-invoices/${id}`, { method: 'DELETE' })
      if ('error' in res) throw new Error(friendlyError(res.error))
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fee-invoices'] }),
    onError: (err) => alert(err instanceof Error ? err.message : 'Could not delete invoice'),
  })

  const [reminderFor, setReminderFor] = useState<InvoiceRow | null>(null)
  const [payFor, setPayFor] = useState<InvoiceRow | null>(null)

  // Summary cards reflect the whole academy, not the current search/status view,
  // so they stay stable while the admin filters the table below.
  const summary = useQuery({
    queryKey: ['fee-invoices', 'summary'],
    queryFn: async () => {
      const res = await api<InvoiceRow[]>('/api/v1/fee-invoices')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const counts = summary.data
    ? {
        pending: summary.data.filter((i) => i.status === 'pending').length,
        overdue: summary.data.filter((i) => i.status === 'overdue').length,
        paid: summary.data.filter((i) => i.status === 'paid').length,
      }
    : null

  // Collected = actual money in (sum of payments), so a partial invoice counts
  // its paid portion. Outstanding = the balance left on any non-settled invoice.
  const totalCollectedPaise =
    summary.data?.reduce((sum, i) => sum + (i.amountPaidInr ?? 0), 0) ?? 0

  const totalOutstandingPaise =
    summary.data
      ?.filter((i) => i.status === 'pending' || i.status === 'overdue' || i.status === 'partial')
      .reduce((sum, i) => sum + i.balanceInr, 0) ?? 0

  const columns: Column<InvoiceRow>[] = [
    {
      key: 'invoiceNo',
      header: 'Invoice',
      render: (i) => (
        <span className="font-mono text-xs text-fg-muted whitespace-nowrap">{i.invoiceNo}</span>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (i) => (
        <div>
          <div className="font-medium text-fg">{i.studentName}</div>
          <div className="text-xs text-fg-subtle">
            {i.parentName ?? 'Parent'} · {i.feeCategoryName ?? 'Custom'}
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (i) => (
        <div>
          <Money paise={i.finalAmountInr} variant="data" />
          {i.status === 'partial' ? (
            <div className="text-xs text-warning">
              <Money paise={i.balanceInr} variant="data" /> due
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'due',
      header: 'Due',
      align: 'right',
      render: (i) => (
        <span className="text-sm text-fg-muted">
          {new Date(i.dueDate).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (i) => <StatusPill tone={i.status}>{i.status}</StatusPill>,
    },
    {
      key: 'paid',
      header: 'Paid via',
      render: (i) =>
        i.paymentMethod ? (
          <span className="text-xs uppercase text-fg-muted">{i.paymentMethod}</span>
        ) : (
          <span className="text-fg-subtle">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (i) => (
        <div className="flex justify-end gap-2 text-xs">
          {(i.status === 'pending' || i.status === 'overdue' || i.status === 'partial') && (
            <>
              <button
                onClick={() => setReminderFor(i)}
                className="text-fg-muted hover:text-fg"
              >
                Payment request
              </button>
              <button
                onClick={() => setPayFor(i)}
                className="text-fg-muted hover:text-success"
              >
                Record payment
              </button>
            </>
          )}
          {/* Only invoices with no money recorded against them can be deleted —
              a partial or paid invoice has payments that must stay auditable. */}
          {i.status !== 'paid' && i.status !== 'partial' && (
            <button
              onClick={() => {
                if (
                  confirm(
                    `Delete invoice ${i.invoiceNo} for ${i.studentName}? This can't be undone.`,
                  )
                )
                  deleteInvoice.mutate(i.id)
              }}
              className="text-fg-muted hover:text-danger"
            >
              Delete
            </button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        title="Fees"
        description="Invoices raised against students. Send reminders or record cash payments here."
        actions={
          <div className="flex items-center gap-2">
            <LinkButton to="/fees/categories" variant="secondary" size="sm">
              Categories
            </LinkButton>
            <LinkButton to="/fees/new">New invoice</LinkButton>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Collected</p>
            <p className="mt-1 text-2xl font-semibold text-success">
              <Money paise={totalCollectedPaise} />
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Outstanding</p>
            <p className="mt-1 text-2xl font-semibold text-warning">
              <Money paise={totalOutstandingPaise} />
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Pending</p>
            <p className="mt-1 text-2xl font-semibold text-fg">
              {counts ? counts.pending : '—'}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Overdue</p>
            <p
              className={`mt-1 text-2xl font-semibold ${
                counts?.overdue ? 'text-danger' : 'text-fg'
              }`}
            >
              {counts ? counts.overdue : '—'}
            </p>
          </CardBody>
        </Card>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input
          type="search"
          placeholder="Search by invoice no, student, parent, category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-sm"
        />
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                statusFilter === f.key
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-surface-subtle text-fg-muted hover:text-fg'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={invoices.data ?? []}
        rowKey={(i) => i.id}
        loading={invoices.isLoading}
        empty={
          debouncedSearch || statusFilter !== 'all'
            ? 'No invoices match your search.'
            : 'No fee invoices yet. Create your first one.'
        }
      />

      <ReminderDialog
        invoice={reminderFor}
        onClose={() => setReminderFor(null)}
        onSent={() => qc.invalidateQueries({ queryKey: ['fee-invoices'] })}
      />

      <RecordPaymentDialog
        invoice={payFor}
        onClose={() => setPayFor(null)}
        onRecorded={() => qc.invalidateQueries({ queryKey: ['fee-invoices'] })}
      />
    </div>
  )
}
